import type {
  ChapterContent,
  ContentBlock,
  FirstGameStep,
  GameManual,
} from '@shared/types';
import { gameManuals } from '@shared/data/gameManuals';
import { perfectRulebooks } from '@shared/data/perfect-quality/generatedRulebooks';
import { getExpansionsForGame } from '@shared/data/expansions';
import { odinChapters } from '@shared/data/a-feast-for-odin/chapters';
import { odinFaq } from '@shared/data/a-feast-for-odin/faq';
import { odinFirstGame } from '@shared/data/a-feast-for-odin/firstGame';
import { odinQuickReference } from '@shared/data/a-feast-for-odin/quickReference';
import { odinGlossary } from '@shared/data/a-feast-for-odin/glossary';
import { odinScoringRecords } from '@shared/data/a-feast-for-odin/scoringTables';
import { barrageScoringRecords } from '@shared/data/barrage/scoringTables';
import { spiritIslandScoringRecords } from '@shared/data/spirit-island/scoringTables';
import { voidfallScoringRecords } from '@shared/data/voidfall/scoringTables';
import { projektGajaScoringRecords } from '@shared/data/projekt-gaja/scoringTables';
import { arkNovaScoringRecords } from '@shared/data/ark-nova/scoringTables';
import { onMarsProgressScoring } from '@shared/data/on-mars/scoringTables';
import { onMarsFaqExtended } from '@shared/data/on-mars/faqExtended';
import { onMarsCommonMistakes } from '@shared/data/on-mars/commonMistakes';
import { onMarsReference } from '@shared/data/on-mars/reference';
import { vinhosRulebook } from '@shared/games/vinhos-deluxe/chapters';
import { vinhosFaqItems } from '@shared/games/vinhos-deluxe/faq';
import { vinhosQuickReferenceSections } from '@shared/games/vinhos-deluxe/quickReference';
import { vinhosFirstGame } from '@shared/games/vinhos-deluxe/firstGame';
import { allRuleSectionsRaw as carcassonneSections } from '@shared/games/carcassonne/data/sections';
import { carcassonneFaq } from '@shared/games/carcassonne/data/faq';
import { carcassonneQuickRef } from '@shared/games/carcassonne/data/quick-ref';
import { carcassonneFirstGameSteps } from '@shared/games/carcassonne/data/first-game';
import { carcassonneGlossary } from '@shared/games/carcassonne/data/glossary';
import { allRuleSectionsRaw as laFamigliaSections } from '@shared/games/la-famiglia/data/sections';
import { laFamigliaFaq } from '@shared/games/la-famiglia/data/faq';
import { laFamigliaQuickReference } from '@shared/games/la-famiglia/data/quick-reference';
import { laFamigliaGlossary } from '@shared/games/la-famiglia/data/glossary';
import { laFamigliaGuidedGame } from '@shared/games/la-famiglia/data/guided';
import { filterGuidedStepsForPlayerCount } from '@shared/games/la-famiglia/data/filtering';

export type PwaGameBundle = {
  manual: GameManual;
  faqBlocks: ContentBlock[];
  quickReferenceBlocks: ContentBlock[];
  glossaryBlocks: ContentBlock[];
  mistakesBlocks: ContentBlock[];
  scoringBlocks: ContentBlock[];
  firstGameSteps: FirstGameStep[];
  expansionTitles: string[];
  searchDocuments: Array<{ id: string; title: string; body: string; href: string }>;
};

type LooseBlock = Record<string, unknown>;

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function paragraphsFromText(text: string): ContentBlock[] {
  return text
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => ({ type: 'paragraph' as const, text: chunk }));
}

/** Normalize game-module content blocks into shared ContentBlock. */
export function normalizeBlock(block: LooseBlock): ContentBlock[] {
  const type = asText(block.type);
  switch (type) {
    case 'heading':
      return [
        {
          type: 'heading',
          text: asText(block.text),
          level: (block.level as 1 | 2 | 3 | undefined) ?? 2,
        },
      ];
    case 'paragraph':
      return [{ type: 'paragraph', text: asText(block.text) }];
    case 'tip':
    case 'note':
      return [{ type: 'tip', text: asText(block.text) }];
    case 'warning':
    case 'commonMistake':
    case 'editionWarning':
      return [{ type: 'warning', text: asText(block.text) }];
    case 'example': {
      const title = asText(block.title);
      const text = asText(block.text);
      return [
        {
          type: 'tip',
          text: title ? `${title}: ${text}` : text,
        },
      ];
    }
    case 'step-list':
    case 'bulletList':
    case 'numberedList':
    case 'checklist':
      return [{ type: 'step-list', steps: asStringArray(block.items ?? block.steps) }];
    case 'table': {
      const headers = asStringArray(block.headers);
      const rows = Array.isArray(block.rows)
        ? (block.rows as unknown[]).map((row) =>
            Array.isArray(row) ? row.map((cell) => String(cell)).join(' | ') : String(row)
          )
        : [];
      return [
        {
          type: 'paragraph',
          text: [headers.join(' | '), ...rows].filter(Boolean).join('\n'),
        },
      ];
    }
    case 'crossReference':
      return [
        {
          type: 'tip',
          text: `Zobacz też: ${asText(block.label) || asText(block.sectionId) || asText(block.chapterId)}`,
        },
      ];
    case 'accordion': {
      const title = asText(block.title);
      const nested = Array.isArray(block.content)
        ? (block.content as LooseBlock[]).flatMap(normalizeBlock)
        : [];
      return [
        { type: 'heading', text: title || 'Szczegóły', level: 3 },
        ...nested,
      ];
    }
    case 'iconRow': {
      const items = Array.isArray(block.items)
        ? (block.items as Array<{ icon?: string; label?: string; value?: string }>)
            .map((item) =>
              [item.icon, item.label, item.value].filter(Boolean).join(' ')
            )
            .filter(Boolean)
        : [];
      return items.length ? [{ type: 'step-list', steps: items }] : [];
    }
    case 'image':
      return [
        {
          type: 'image',
          source: asText(block.source),
          alt: asText(block.alt) || 'Ilustracja',
          caption: asText(block.caption) || undefined,
        },
      ];
    case 'diagram':
      return [
        {
          type: 'tip',
          text: asText(block.fallbackText) || asText(block.alt) || 'Diagram (zobacz aplikację mobilną).',
        },
      ];
    default:
      if (asText(block.text)) {
        return [{ type: 'paragraph', text: asText(block.text) }];
      }
      return [];
  }
}

function scoringTableBlocks(
  records: Array<{ element: string; scoring: string; conditions?: string }>
): ContentBlock[] {
  const blocks: ContentBlock[] = [
    { type: 'heading', text: 'Tabele punktacji', level: 2 },
  ];
  for (const record of records) {
    blocks.push({ type: 'heading', text: record.element, level: 3 });
    blocks.push({ type: 'paragraph', text: record.scoring });
    if (record.conditions) {
      blocks.push({ type: 'tip', text: `Warunki: ${record.conditions}` });
    }
  }
  return blocks;
}

function collectSearchDocs(
  bundle: Omit<PwaGameBundle, 'searchDocuments'>,
  gameId: string
): PwaGameBundle['searchDocuments'] {
  const docs: PwaGameBundle['searchDocuments'] = [];
  bundle.manual.chapters.forEach((chapter, index) => {
    const body = chapter.content
      .map((block) => {
        if ('text' in block && typeof block.text === 'string') return block.text;
        if (block.type === 'step-list') return block.steps.join(' ');
        if (block.type === 'checklist') return block.items.join(' ');
        return '';
      })
      .join(' ');
    docs.push({
      id: `${gameId}-ch-${index}`,
      title: chapter.title,
      body,
      href: `/game/${gameId}/manual/${index}`,
    });
  });

  const pushBlocks = (
    prefix: string,
    title: string,
    blocks: ContentBlock[],
    href: string
  ) => {
    const body = blocks
      .map((block) => {
        if ('text' in block && typeof block.text === 'string') return block.text;
        if (block.type === 'step-list') return block.steps.join(' ');
        return '';
      })
      .join(' ');
    if (body.trim()) {
      docs.push({ id: `${gameId}-${prefix}`, title, body, href });
    }
  };

  pushBlocks('faq', 'FAQ', bundle.faqBlocks, `/game/${gameId}/faq`);
  pushBlocks('qr', 'Szybka ściąga', bundle.quickReferenceBlocks, `/game/${gameId}/quick-reference`);
  pushBlocks('glossary', 'Słownik', bundle.glossaryBlocks, `/game/${gameId}/glossary`);
  pushBlocks('scoring', 'Punktacja', bundle.scoringBlocks, `/game/${gameId}/scoring`);
  return docs;
}

function odinToManual(): GameManual {
  const chapters: ChapterContent[] = odinChapters.map((chapter) => ({
    title: chapter.title,
    content: [
      { type: 'paragraph', text: chapter.summary },
      ...chapter.sections.flatMap((section) => [
        { type: 'heading' as const, text: section.title, level: 2 as const },
        ...paragraphsFromText(section.body),
      ]),
    ],
  }));

  return {
    chapters,
    firstGameSteps: odinFirstGame.steps.map((step, index) => ({
      id: index + 1,
      title: step.title,
      content: [step.body],
      why: '',
      commonMistake: '',
    })),
  };
}

function vinhosToManual(): GameManual {
  const chapters: ChapterContent[] = vinhosRulebook.chapters.map((chapter) => ({
    title: chapter.title,
    content: chapter.sections.flatMap((section) => [
      { type: 'heading' as const, text: section.title, level: 2 as const },
      ...section.content.flatMap((block) => normalizeBlock(block as LooseBlock)),
    ]),
  }));

  return {
    chapters,
    firstGameSteps: vinhosFirstGame.steps.map((step, index) => ({
      id: index + 1,
      title: step.title,
      content: step.checklist?.length
        ? step.checklist
        : [step.summary, step.whyContent].filter(Boolean),
      why: step.whyContent ?? '',
      commonMistake: step.commonMistake ?? '',
    })),
  };
}

function sectionsToManual(
  sections: Array<{ title: string; content: LooseBlock[] }>
): GameManual {
  return {
    chapters: sections.map((section) => ({
      title: section.title,
      content: section.content.flatMap(normalizeBlock),
    })),
    firstGameSteps: [],
  };
}

export function getPwaGameBundle(gameId: string): PwaGameBundle {
  const expansions = getExpansionsForGame(gameId).map((item) => item.title);
  const baseManual = gameManuals[gameId];
  const perfect = perfectRulebooks[gameId];

  let manual: GameManual =
    baseManual && baseManual.chapters.length > 0
      ? baseManual
      : perfect
        ? {
            chapters: perfect.chapters,
            firstGameSteps: baseManual?.firstGameSteps ?? [],
          }
        : { chapters: [], firstGameSteps: [] };

  let faqBlocks: ContentBlock[] = perfect?.faq ?? [];
  let quickReferenceBlocks: ContentBlock[] = perfect?.quickReference ?? [];
  let glossaryBlocks: ContentBlock[] = perfect?.glossary ?? [];
  let mistakesBlocks: ContentBlock[] = perfect?.mistakes ?? [];
  let scoringBlocks: ContentBlock[] = perfect?.scoring ?? [];
  let firstGameSteps: FirstGameStep[] = manual.firstGameSteps ?? [];

  if (gameId === 'uczta-dla-odyna') {
    manual = odinToManual();
    firstGameSteps = manual.firstGameSteps;
    faqBlocks = odinFaq.flatMap((entry) => [
      { type: 'heading' as const, text: entry.question, level: 3 as const },
      ...paragraphsFromText(entry.answer),
    ]);
    quickReferenceBlocks = odinQuickReference.flatMap((entry) => [
      { type: 'heading' as const, text: entry.title, level: 3 as const },
      { type: 'step-list' as const, steps: [...entry.items] },
    ]);
    glossaryBlocks = odinGlossary.flatMap((entry) => [
      { type: 'heading' as const, text: entry.term, level: 3 as const },
      ...paragraphsFromText(entry.definition),
    ]);
    scoringBlocks = scoringTableBlocks(odinScoringRecords);
  }

  if (gameId === 'vinhos-deluxe') {
    manual = vinhosToManual();
    firstGameSteps = manual.firstGameSteps;
    faqBlocks = vinhosFaqItems.flatMap((entry) => [
      { type: 'heading' as const, text: entry.question, level: 3 as const },
      ...paragraphsFromText(entry.answer),
    ]);
    quickReferenceBlocks = vinhosQuickReferenceSections.flatMap((entry) => [
      { type: 'heading' as const, text: entry.title, level: 3 as const },
      ...paragraphsFromText(entry.content),
    ]);
  }

  if (gameId === 'carcassonne') {
    manual = sectionsToManual(carcassonneSections);
    faqBlocks = carcassonneFaq.flatMap((entry) => [
      { type: 'heading' as const, text: entry.question, level: 3 as const },
      ...paragraphsFromText(entry.answer),
    ]);
    quickReferenceBlocks = carcassonneQuickRef.flatMap((entry) => [
      { type: 'heading' as const, text: entry.title, level: 3 as const },
      ...entry.blocks.flatMap((block) => normalizeBlock(block as LooseBlock)),
    ]);
    glossaryBlocks = carcassonneGlossary.flatMap((entry) => [
      { type: 'heading' as const, text: entry.term, level: 3 as const },
      ...paragraphsFromText(entry.definition),
    ]);
    firstGameSteps = carcassonneFirstGameSteps.map((step, index) => ({
      id: index + 1,
      title: step.title,
      content: step.content,
      why: step.why,
      commonMistake: step.commonMistake,
    }));
  }

  if (gameId === 'la-famiglia') {
    manual = sectionsToManual(laFamigliaSections);
    faqBlocks = laFamigliaFaq.flatMap((entry) => [
      { type: 'heading' as const, text: entry.question, level: 3 as const },
      ...paragraphsFromText(entry.answer),
    ]);
    quickReferenceBlocks = laFamigliaQuickReference.flatMap((entry) => [
      { type: 'heading' as const, text: entry.title, level: 3 as const },
      ...entry.content.flatMap((block) => normalizeBlock(block as LooseBlock)),
    ]);
    glossaryBlocks = laFamigliaGlossary.flatMap((entry) => [
      { type: 'heading' as const, text: entry.term, level: 3 as const },
      ...paragraphsFromText(entry.definition),
    ]);
    // Expo first-game uses guided steps filtered by player count (default 4).
    firstGameSteps = filterGuidedStepsForPlayerCount(
      laFamigliaGuidedGame.steps,
      4
    ).map((step, index) => ({
      id: index + 1,
      title: step.title,
      content: [
        step.summary,
        ...(step.checklist?.length
          ? step.checklist.map((item) => `• ${item}`)
          : []),
      ].filter(Boolean),
      why: step.whyContent ?? '',
      commonMistake: step.commonMistake ?? '',
    }));
  }

  if (scoringBlocks.length === 0) {
    if (gameId === 'barrage') scoringBlocks = scoringTableBlocks(barrageScoringRecords);
    if (gameId === 'spirit-island')
      scoringBlocks = scoringTableBlocks(spiritIslandScoringRecords);
    if (gameId === 'voidfall') scoringBlocks = scoringTableBlocks(voidfallScoringRecords);
    if (gameId === 'projekt-gaja')
      scoringBlocks = scoringTableBlocks(projektGajaScoringRecords);
    if (gameId === 'ark-nova') scoringBlocks = scoringTableBlocks(arkNovaScoringRecords);
    if (gameId === 'on-mars') {
      scoringBlocks = [
        { type: 'heading', text: 'Postęp (kostki)', level: 2 },
        {
          type: 'step-list',
          steps: onMarsProgressScoring.map(
            (row) => `${row.label}: ${row.points} OP${row.notes ? ` — ${row.notes}` : ''}`
          ),
        },
      ];
      faqBlocks = onMarsFaqExtended.flatMap((entry) => [
        { type: 'heading' as const, text: entry.question, level: 3 as const },
        ...paragraphsFromText(entry.answer),
      ]);
      mistakesBlocks = onMarsCommonMistakes.flatMap((entry) => [
        { type: 'heading' as const, text: entry.title, level: 3 as const },
        ...paragraphsFromText(entry.body),
        ...(entry.warnings ?? []).map((warning) => ({
          type: 'warning' as const,
          text: warning,
        })),
      ]);
      glossaryBlocks = onMarsReference.flatMap((entry) => [
        { type: 'heading' as const, text: entry.title, level: 3 as const },
        { type: 'paragraph' as const, text: entry.short },
        ...entry.details.map((detail) => ({
          type: 'paragraph' as const,
          text: detail,
        })),
      ]);
    }
  }

  if ((!firstGameSteps || firstGameSteps.length === 0) && perfect?.firstGame?.length) {
    firstGameSteps = [
      {
        id: 1,
        title: 'Pierwsza gra',
        content: perfect.firstGame
          .map((block) => {
            if ('text' in block && typeof block.text === 'string') return block.text;
            if (block.type === 'step-list') return block.steps.join(' ');
            return '';
          })
          .filter(Boolean),
        why: '',
        commonMistake: '',
      },
    ];
  }

  const partial = {
    manual: { ...manual, firstGameSteps },
    faqBlocks,
    quickReferenceBlocks,
    glossaryBlocks,
    mistakesBlocks,
    scoringBlocks,
    firstGameSteps,
    expansionTitles: expansions,
  };

  return {
    ...partial,
    searchDocuments: collectSearchDocs(partial, gameId),
  };
}
